import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { buildApiUrl } from '../utils/apiConfig';
import { formatDateOfBirthUs } from '../utils/dateDisplay';
import ProfileAvatar from './ProfileAvatar';
import BrandBanner from './BrandBanner';
import { SHOW_ATHLETE_PROFILE_ZIP_FIELD } from '../constants/athleteProfileFeatures';
import '../styles/athlete-profile-readonly.css';
import { apiClient } from "../utils/apiClient";

interface StaffMemberData {
    userId?: number;
    firstName?: string;
    lastName?: string;
    displayName?: string;
    email?: string;
    telephone?: string;
    title?: string;
    role?: string;
    profileImageUrl?: string;
}

interface AthleteProfileData {
    id?: string;
    userId?: number;
    firstName?: string;
    lastName?: string;
    nickname?: string;
    sport?: string;
    graduationYear?: string;
    school?: string;
    phone?: string;
    email?: string;
    parentEmail?: string;
    parentEmail2?: string;
    parentPhone?: string;
    dateOfBirth?: string;
    program?: string;
    location?: string;
    // City support
    cityName?: string;
    /** Primary address ZIP from shared profile API */
    postalCode?: string;
    pronouns?: string;
    profileImageUrl?: string;
    imageUrl?: string;
    athletePhone?: string;
    parentMobile?: string;
    instagramHandle?: string;
    tiktokHandle?: string;
    linkedinUrl?: string;
    facebookName?: string;
    hudlUrl?: string;
    xHandle?: string;
    staffMember?: StaffMemberData | null;
}

export default function AthleteProfileReadOnly() {
    const { athleteName, uuid } = useParams<{ athleteName: string; uuid: string }>();
    const [athleteData, setAthleteData] = useState<AthleteProfileData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchSharedProfile = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await apiClient(
                    buildApiUrl(`api/athletes/profile/shared/${athleteName}/${uuid}`),
                    {
                        method: 'GET',
                        headers: {
                            'Accept': 'application/json'
                        }
                    }
                );

                if (response.ok) {
                    const data = await response.json();
                    setAthleteData(data);
                } else if (response.status === 404) {
                    setError('Profile not found or link has expired');
                } else {
                    setError('Failed to load profile');
                }
            } catch (err) {
                console.error('Failed to fetch shared profile:', err);
                setError('Failed to load profile');
            } finally {
                setLoading(false);
            }
        };

        if (athleteName && uuid) {
            fetchSharedProfile();
        }
    }, [athleteName, uuid]);

    if (loading) {
        return (
            <div className="readonly-profile-container">
                <div className="readonly-loading">Loading profile...</div>
            </div>
        );
    }

    if (error || !athleteData) {
        return (
            <div className="readonly-profile-container">
                <div className="readonly-error">{error || 'Profile not found'}</div>
            </div>
        );
    }

    const displayName = `${athleteData.firstName || ''} ${athleteData.lastName || ''}`.trim() || 'Athlete';
    const graduationYear = athleteData.graduationYear || '';
    // Only show sport in the badge, don't fallback to program
    const sport = athleteData.sport || '';

    // Build banner subtitle: graduation year class only (sport is shown in the profile card)
    const classOf = graduationYear ? `Class of ${graduationYear}` : '';
    const bannerSubtitle = classOf || 'ATHLETE';

    return (
        <div className="readonly-profile-wrapper">
            <BrandBanner
                displayName={displayName}
                hasProfile={true}
                className="readonly-brand-banner"
                profileImageUrl={athleteData.profileImageUrl}
                subtitleOverride={bannerSubtitle}
            />

            <div className="readonly-main-content">
                <div className="readonly-profile-card">
                    <div className="readonly-card-header">
                        <div className="readonly-card-info">
                            <div className="readonly-avatar">
                                <ProfileAvatar
                                    displayName={displayName}
                                    size="large"
                                    className="readonly-avatar-img"
                                    alt={displayName}
                                    allowUpload={false}
                                    userId={athleteData?.userId}
                                    imageUrl={athleteData?.profileImageUrl}
                                />
                            </div>
                            <div className="readonly-details">
                                <h2 className="readonly-name">{displayName}</h2>
                                {sport && (
                                    <span className="readonly-sport-tag">{sport}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="readonly-info-grid">
                        {/* Personal Information Section */}
                        <div className="readonly-profile-section-content">
                            <div className="readonly-profile-section-header section-personal">
                                <span className="section-icon">👤</span>
                                <span>Personal Information</span>
                            </div>

                            <div className="readonly-info-row">
                                <div className="readonly-info-item">
                                    <label>First Name</label>
                                    <span>{athleteData.firstName || '-'}</span>
                                </div>
                                <div className="readonly-info-item">
                                    <label>Last Name</label>
                                    <span>{athleteData.lastName || '-'}</span>
                                </div>
                            </div>

                            <div className="readonly-info-row">
                                <div className="readonly-info-item">
                                    <label>Date of Birth</label>
                                    <span>{formatDateOfBirthUs(athleteData.dateOfBirth) ?? '-'}</span>
                                </div>
                                <div className="readonly-info-item">
                                    <label>Nickname</label>
                                    <span>{athleteData.nickname || '-'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Location Section */}
                        <div className="readonly-profile-section-content">
                            <div className="readonly-profile-section-header section-location">
                                <span className="section-icon">📍</span>
                                <span>Location</span>
                            </div>

                            {/* One full-width row: 4 equal columns — matches Personal (2×2 half-width rows = same 4 column positions) */}
                            <div className="readonly-info-row readonly-info-row--location-four">
                                {SHOW_ATHLETE_PROFILE_ZIP_FIELD && (
                                    <div className="readonly-info-item">
                                        <label>ZIP code</label>
                                        <span>{athleteData.postalCode || '-'}</span>
                                    </div>
                                )}
                                <div className="readonly-info-item">
                                    <label>City</label>
                                    <span>{athleteData.cityName || '-'}</span>
                                </div>
                                <div className="readonly-info-item">
                                    <label>State</label>
                                    <span>{athleteData.location || '-'}</span>
                                </div>
                                <div
                                    className="readonly-info-item readonly-info-item--grid-spacer"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>

                        {/* Academic & Sport Section */}
                        <div className="readonly-profile-section-content">
                            <div className="readonly-profile-section-header section-academic">
                                <span className="section-icon">🏆</span>
                                <span>Academic & Sport</span>
                            </div>

                            <div className="readonly-info-row">
                                <div className="readonly-info-item">
                                    <label>School</label>
                                    <span>{athleteData.school || '-'}</span>
                                </div>
                                <div className="readonly-info-item">
                                    <label>Graduation Year</label>
                                    <span>{graduationYear || '-'}</span>
                                </div>
                            </div>

                            <div className="readonly-info-row">
                                <div className="readonly-info-item">
                                    <label>Sport</label>
                                    <span>{sport || '-'}</span>
                                </div>
                                <div className="readonly-info-item">
                                    <label>Program / Club</label>
                                    <span>{athleteData.program || sport || '-'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Athlete Contact Section */}
                        <div className="readonly-profile-section-content">
                            <div className="readonly-profile-section-header section-contact">
                                <span className="section-icon">📱</span>
                                <span>Athlete Contact</span>
                            </div>

                            <div className="readonly-info-row">
                                <div className="readonly-info-item">
                                    <label>Email</label>
                                    <span>{athleteData.email || '-'}</span>
                                </div>
                                <div className="readonly-info-item">
                                    <label>Mobile</label>
                                    <span>{athleteData.phone || athleteData.athletePhone || '-'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Parent / Guardian Contact Section */}
                        <div className="readonly-profile-section-content">
                            <div className="readonly-profile-section-header section-parent">
                                <span className="section-icon">👥</span>
                                <span>Parent / Guardian Contact</span>
                            </div>

                            <div className="readonly-info-row">
                                <div className="readonly-info-item">
                                    <label>Email</label>
                                    <span>{athleteData.parentEmail || '-'}</span>
                                </div>
                                <div className="readonly-info-item">
                                    <label>Email 2</label>
                                    <span>{athleteData.parentEmail2 || '-'}</span>
                                </div>
                            </div>
                            <div className="readonly-info-row">
                                <div className="readonly-info-item">
                                    <label>Mobile</label>
                                    <span>{athleteData.parentPhone || athleteData.parentMobile || '-'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Social Section - Always show */}
                <div className="readonly-social-section">
                    <div className="readonly-section-header">
                        <h3>Social</h3>
                    </div>
                    <div className="readonly-social-links">
                        <div className="readonly-social-item">
                            <div className="readonly-social-icon instagram">📷</div>
                            <div className="readonly-social-info">
                                <span className="readonly-social-platform">Instagram</span>
                                {athleteData.instagramHandle ? (
                                    <a
                                        href={`https://instagram.com/${athleteData.instagramHandle.replace('@', '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="readonly-social-link"
                                    >
                                        @{athleteData.instagramHandle.replace('@', '')}
                                    </a>
                                ) : (
                                    <span className="readonly-social-handle">-</span>
                                )}
                            </div>
                        </div>

                        <div className="readonly-social-item">
                            <div className="readonly-social-icon tiktok">🎵</div>
                            <div className="readonly-social-info">
                                <span className="readonly-social-platform">TikTok</span>
                                {athleteData.tiktokHandle ? (
                                    <a
                                        href={`https://tiktok.com/@${athleteData.tiktokHandle.replace('@', '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="readonly-social-link"
                                    >
                                        @{athleteData.tiktokHandle.replace('@', '')}
                                    </a>
                                ) : (
                                    <span className="readonly-social-handle">-</span>
                                )}
                            </div>
                        </div>

                        <div className="readonly-social-item">
                            <div className="readonly-social-icon linkedin">💼</div>
                            <div className="readonly-social-info">
                                <span className="readonly-social-platform">LinkedIn</span>
                                {athleteData.linkedinUrl ? (
                                    <a
                                        href={athleteData.linkedinUrl.startsWith('http') ? athleteData.linkedinUrl : `https://${athleteData.linkedinUrl}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="readonly-social-link"
                                    >
                                        {athleteData.linkedinUrl}
                                    </a>
                                ) : (
                                    <span className="readonly-social-handle">-</span>
                                )}
                            </div>
                        </div>

                        <div className="readonly-social-item">
                            <div className="readonly-social-icon facebook">f</div>
                            <div className="readonly-social-info">
                                <span className="readonly-social-platform">Facebook</span>
                                {athleteData.facebookName ? (
                                    <a
                                        href={`https://facebook.com/${encodeURIComponent(athleteData.facebookName.trim())}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="readonly-social-link"
                                    >
                                        {athleteData.facebookName}
                                    </a>
                                ) : (
                                    <span className="readonly-social-handle">-</span>
                                )}
                            </div>
                        </div>

                        <div className="readonly-social-item">
                            <div className="readonly-social-icon hudl">🏆</div>
                            <div className="readonly-social-info">
                                <span className="readonly-social-platform">Hudl</span>
                                {athleteData.hudlUrl ? (
                                    <a
                                        href={athleteData.hudlUrl.startsWith('http') ? athleteData.hudlUrl : `https://${athleteData.hudlUrl}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="readonly-social-link"
                                    >
                                        {athleteData.hudlUrl}
                                    </a>
                                ) : (
                                    <span className="readonly-social-handle">-</span>
                                )}
                            </div>
                        </div>

                        <div className="readonly-social-item">
                            <div className="readonly-social-icon x-twitter">𝕏</div>
                            <div className="readonly-social-info">
                                <span className="readonly-social-platform">X</span>
                                {athleteData.xHandle ? (
                                    <a
                                        href={`https://x.com/${athleteData.xHandle.replace(/^@/, '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="readonly-social-link"
                                    >
                                        @{athleteData.xHandle.replace(/^@/, '')}
                                    </a>
                                ) : (
                                    <span className="readonly-social-handle">-</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Staff Member Section - Show when staffMember data exists */}
                {athleteData.staffMember && (
                    <div className="readonly-staff-section">
                        <div className="readonly-staff-card">
                            <div className="readonly-staff-avatar-col">
                                <ProfileAvatar
                                    displayName={athleteData.staffMember.displayName ||
                                        `${athleteData.staffMember.firstName || ''} ${athleteData.staffMember.lastName || ''}`.trim() ||
                                        'Staff Member'}
                                    size="large"
                                    className="readonly-staff-avatar"
                                    alt={athleteData.staffMember.displayName || 'Staff Member'}
                                    allowUpload={false}
                                    userId={athleteData.staffMember.userId}
                                    imageUrl={athleteData.staffMember.profileImageUrl}
                                />
                            </div>

                            <div className="readonly-staff-info-col">
                                <div className="readonly-staff-title-block">
                                    <h3 className="readonly-staff-name">
                                        {athleteData.staffMember.displayName ||
                                            `${athleteData.staffMember.firstName || ''} ${athleteData.staffMember.lastName || ''}`.trim() ||
                                            'Staff Member'}
                                    </h3>
                                    {athleteData.staffMember.role?.toLowerCase().includes('admin') && (
                                        <span className="readonly-staff-badge">Admin</span>
                                    )}
                                </div>

                                {athleteData.staffMember.title && (
                                    <div className="readonly-staff-subtitle">{athleteData.staffMember.title}</div>
                                )}

                                <div className="readonly-staff-contact-row">
                                    <div className="readonly-staff-field">
                                        <span className="readonly-staff-label">Email</span>
                                        <span className="readonly-staff-value">{athleteData.staffMember.email || '—'}</span>
                                    </div>
                                    <div className="readonly-staff-field">
                                        <span className="readonly-staff-label">Phone Number</span>
                                        <span className="readonly-staff-value">{athleteData.staffMember.telephone || '—'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
